import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Components
import { BlockInteractions, createButtonObject, DISPLAY_TYPE, Views } from '../../organisms/BlockInteractions';
import BlockFetcher from '../../hocs/BlockFetcher/BlockFetcher';

import { Icons } from '../../atoms/SVG';
import SingleInputDefault from './partials/SingleInputDefault/SingleInputDefault';

import { Namespace } from '../../../translation/namespace';
import { BlockType } from '../../../types/api/ElementType';
import GenericForm from './GenericForm/GenericForm';
import CspEditForm from './CspEditForm/CspEditForm';

const DefaultView = BlockFetcher()(SingleInputDefault);
const EditView = BlockFetcher()(GenericForm);
const CspEditView = BlockFetcher()(CspEditForm);

function SingleInputBlock({ activeView = null, uuid, elementType, views }) {
  const { t } = useTranslation();

  const getTranslationNamespace = () => {
    switch (elementType) {
      case BlockType.portaltitle:
        return Namespace.PortalTitle;
      case BlockType.changepasswordurl:
        return Namespace.ChangePasswordUrl;
      case BlockType.goliveMode_enabled:
        return Namespace.GoliveModeEnabled;
      case BlockType.anonymousaccess_enabled:
        return Namespace.AnonymousAccessEnabled;
      case BlockType.userregistration_enabled:
        return Namespace.UserRegistrationEnabled;
      case BlockType.inviteunregistered_enabled:
        return Namespace.InviteUnregisteredEnabled;
      case BlockType.sessionrestrictor_enabled:
        return Namespace.SessionRestrictorEnabled;
      case BlockType.portaluserregistration_enabled:
        return Namespace.PortalUserRegistrationEnabled;
      case BlockType.supportEmail:
        return Namespace.SupportEmail;
      case BlockType.portalInformationLink:
        return Namespace.PortalInformationLink;
      case BlockType.highestConfidentialLevel:
        return Namespace.HighestConfidentialLevel;
      case BlockType.defaultConfidentialLevel:
        return Namespace.DefaultConfidentialLevel;
      case BlockType.printingDependsOnConfidentialLevels:
        return Namespace.PrintingDependsOnConfidentialLevels;
      case BlockType.extendedProtocol_readAccess_enabled:
        return Namespace.ExtendedProtocolReadAccessEnabled;
      case BlockType.extendedProtocol_writeAccess_adminContent_enabled:
        return Namespace.ExtendedProtocolWriteAccessAdminContentEnabled;
      case BlockType.extendedProtocol_writeAccess_userContent_enabled:
        return Namespace.ExtendedProtocolWriteAccessUserContentEnabled;
      case BlockType.allowedmimetypes:
        return Namespace.AllowedMimeTypes;
      case BlockType.cspFrameSourceConfig:
        return Namespace.CspFrameSourceConfig;
      case BlockType.defaultForMembersWithParentTask:
        return Namespace.DefaultForMembersWithParentTask;
      case BlockType.defaultForMembersWithParentTeamroom:
        return Namespace.DefaultForMembersWithParentTeamroom;
      case BlockType.defaultForMembersWithParentTopic:
        return Namespace.DefaultForMembersWithParentTopic;
      case BlockType.defaultForMembersWithParentOrgunit:
        return Namespace.DefaultForMembersWithParentOrgunit;
      case BlockType.defaultForMembersWithParentProcess:
        return Namespace.DefaultForMembersWithParentProcess;
      case BlockType.defaultForMembersWithParentLocation:
        return Namespace.DefaultForMembersWithParentLocation;
      case BlockType.defaultForNewsletterFrequency:
        return Namespace.DefaultForNewsletterFrequency;
      case BlockType.depublishPagesWithExceededValidityPeriod_enabled:
        return Namespace.DepublishPagesWithExceededValidityPeriodEnabled;
      case BlockType.pageLockValidityPeriod:
        return Namespace.PageLockValidityPeriod;
      case BlockType.userImportSystemService_enabled:
        return Namespace.UserImportSystemServiceEnabled;
      case BlockType.pageMembershipMatcherSystemService_enabled:
        return Namespace.PageMembershipMatcherSystemServiceEnabled;
      default:
        return '';
    }
  };

  const headline = {
    text: t(`${getTranslationNamespace()}:headline`),
    hierarchy: 2,
    visible: true,
  };

  const getEditViewByBlockType = () => {
    if (elementType === BlockType.cspFrameSourceConfig) {
      return <CspEditView {...views.edit} />;
    }

    return (
      <EditView
        additionalProps={{
          id: 'config-value-update-form',
          translationBaseKey: `${getTranslationNamespace()}:edit`,
          hideHeadline: true,
          hideFormHelpPhrase: true,
        }}
        {...views.edit}
      />
    );
  };

  const createButtonsObject = () => {
    const EditButton = createButtonObject(
      Views.EDIT,
      Icons.EDIT_PENCIL,
      `${getTranslationNamespace()}:edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      getEditViewByBlockType(),
    );

    const DeleteButton = createButtonObject(
      Views.DELETE,
      Icons.ERASER,
      `${getTranslationNamespace()}:delete`,
      DISPLAY_TYPE.MODAL,
    );
    return Object.assign(EditButton, DeleteButton);
  };

  return (
    <BlockInteractions
      headline={headline}
      alwaysShowInfo
      views={views}
      activeView={activeView}
      buttons={createButtonsObject()}
      uuid={uuid}
      elementType={elementType}
    >
      <DefaultView
        additionalProps={{
          translationNamespace: getTranslationNamespace(),
        }}
        {...views.default}
      />
    </BlockInteractions>
  );
}

SingleInputBlock.propTypes = {
  activeView: PropTypes.string,
  uuid: PropTypes.string.isRequired,
  elementType: PropTypes.string.isRequired,
  views: PropTypes.shape({
    default: PropTypes.shape({}),
    edit: PropTypes.shape({}),
  }).isRequired,
};

export default BlockFetcher()(SingleInputBlock);
